import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Sparkles, LogOut, Users, Search, Filter, Download } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [activeTab, setActiveTab] = useState('leads'); // leads or queries

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [leadsRes, bookingsRes, queriesRes] = await Promise.all([
        axios.get(`${API}/leads/all`, config),
        axios.get(`${API}/bookings/all`, config),
        axios.get(`${API}/queries/all`, config)
      ]);

      setLeads(leadsRes.data);
      setBookings(bookingsRes.data);
      setQueries(queriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
    toast.success('Logged out successfully');
  };

  const exportToCSV = () => {
    const csvData = filteredLeads.map(lead => ({
      Name: lead.name || 'N/A',
      Age: lead.age || 'N/A',
      Contact: lead.phone,
      Occupation: lead.occupation || 'N/A',
      Location: lead.location || 'N/A',
      Issues: lead.health_issues || 'N/A',
      Stage: lead.stage,
      Date: lead.captured_at ? format(new Date(lead.captured_at), 'MMM dd, yyyy') : 'N/A'
    }));

    const headers = Object.keys(csvData[0]).join(',');
    const rows = csvData.map(row => Object.values(row).join(',')).join('\n');
    const csv = `${headers}\n${rows}`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `divine-quanta-leads-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    toast.success('Data exported successfully!');
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.phone?.includes(searchTerm) ||
                         lead.occupation?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStage === 'all' || lead.stage === filterStage;
    return matchesSearch && matchesFilter;
  });

  const getStageColor = (stage) => {
    const colors = {
      initial: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      form_filled: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      payment_pending: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      completed: 'bg-green-500/20 text-green-300 border-green-500/30'
    };
    return colors[stage] || colors.initial;
  };

  const stats = {
    total: leads.length,
    completed: leads.filter(l => l.stage === 'completed').length,
    pending: leads.filter(l => l.stage !== 'completed').length,
    initial: leads.filter(l => l.stage === 'initial').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-950 via-indigo-950 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-950 via-indigo-950 to-purple-900">
      {/* Header */}
      <header className="border-b border-purple-500/30 bg-purple-900/40 backdrop-blur-lg">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Sparkles className="w-8 h-8 text-purple-300" />
              <div>
                <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                  Admin Dashboard
                </h1>
                <p className="text-purple-300 text-sm">Complete Business Overview</p>
              </div>
            </div>
            <Button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              variant="ghost"
              className="text-purple-200 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-6" data-testid="stat-total-leads">
            <p className="text-purple-300 text-sm mb-1">Total Leads</p>
            <p className="text-3xl font-bold text-white">{stats.total}</p>
          </Card>
          <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-6" data-testid="stat-completed">
            <p className="text-purple-300 text-sm mb-1">Completed Bookings</p>
            <p className="text-3xl font-bold text-green-300">{stats.completed}</p>
          </Card>
          <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-6" data-testid="stat-pending">
            <p className="text-purple-300 text-sm mb-1">Pending/Incomplete</p>
            <p className="text-3xl font-bold text-orange-300">{stats.pending}</p>
          </Card>
          <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-6" data-testid="stat-initial">
            <p className="text-purple-300 text-sm mb-1">Initial Contact</p>
            <p className="text-3xl font-bold text-yellow-300">{stats.initial}</p>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-400 w-5 h-5" />
                <Input
                  placeholder="Search by name, phone, or occupation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-purple-950/50 border-purple-500/30 text-white"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <select
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
                className="bg-purple-950/50 border border-purple-500/30 text-white rounded-lg px-4 py-2"
                data-testid="filter-stage"
              >
                <option value="all">All Stages</option>
                <option value="initial">Initial Contact</option>
                <option value="form_filled">Form Filled</option>
                <option value="payment_pending">Payment Pending</option>
                <option value="completed">Completed</option>
              </select>
              <Button
                onClick={exportToCSV}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <div className="flex space-x-4 mb-8">
          <Button
            onClick={() => setActiveTab('leads')}
            className={`px-8 py-4 rounded-lg font-semibold ${
              activeTab === 'leads'
                ? 'bg-purple-600 text-white'
                : 'bg-purple-900/40 text-purple-300 hover:bg-purple-900/60'
            }`}
            data-testid="tab-leads"
          >
            Booking Leads ({leads.length})
          </Button>
          <Button
            onClick={() => setActiveTab('queries')}
            className={`px-8 py-4 rounded-lg font-semibold ${
              activeTab === 'queries'
                ? 'bg-purple-600 text-white'
                : 'bg-purple-900/40 text-purple-300 hover:bg-purple-900/60'
            }`}
            data-testid="tab-queries"
          >
            General Queries ({queries.length})
          </Button>
        </div>

        {/* Leads Table */}
        {activeTab === 'leads' && (
          <>
            <h2 className="text-3xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              All Booking Leads
            </h2>

        {filteredLeads.length === 0 ? (
          <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-12 text-center">
            <Users className="w-16 h-16 text-purple-400 mx-auto mb-4" />
            <p className="text-purple-200 text-lg">No leads found</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredLeads.map((lead) => (
              <Card
                key={lead.id || lead.phone}
                className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-6"
                data-testid={`lead-card-${lead.phone}`}
              >
                <div className="grid md:grid-cols-7 gap-6 items-center">
                  <div>
                    <p className="text-purple-400 text-sm mb-1">Name</p>
                    <p className="text-white font-semibold">{lead.name || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-purple-400 text-sm mb-1">Age</p>
                    <p className="text-white">{lead.age || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-purple-400 text-sm mb-1">Contact</p>
                    <p className="text-white font-mono text-sm">{lead.phone}</p>
                  </div>
                  <div>
                    <p className="text-purple-400 text-sm mb-1">WhatsApp</p>
                    <p className="text-green-300 font-mono text-sm">{lead.whatsapp || lead.phone}</p>
                  </div>
                  <div>
                    <p className="text-purple-400 text-sm mb-1">Occupation</p>
                    <p className="text-white">{lead.occupation || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-purple-400 text-sm mb-1">Location</p>
                    <p className="text-white">{lead.location || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-purple-400 text-sm mb-1">Stage</p>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs border ${getStageColor(lead.stage)}`}>
                      {lead.stage?.replace('_', ' ').toUpperCase() || 'INITIAL'}
                    </span>
                  </div>
                </div>
                {lead.health_issues && (
                  <div className="mt-4 pt-4 border-t border-purple-500/30">
                    <p className="text-purple-400 text-sm mb-2">Health Issues / Concerns</p>
                    <p className="text-purple-200">{lead.health_issues}</p>
                  </div>
                )}
                <div className="mt-4 pt-4 border-t border-purple-500/30 flex justify-between items-center">
                  <p className="text-purple-400 text-sm">
                    Captured: {lead.captured_at ? format(new Date(lead.captured_at), 'MMM dd, yyyy hh:mm a') : 'N/A'}
                  </p>
                  <a
                    href={`https://wa.me/${lead.whatsapp || lead.phone}?text=Hi%20${lead.name},%20this%20is%20from%20Divine%20Quanta.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    Send Report on WhatsApp
                  </a>
                </div>
              </Card>
            ))}
          </div>
        )}
        </>
        )}

        {/* Queries Table */}
        {activeTab === 'queries' && (
          <>
            <h2 className="text-3xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              General Queries from Clients
            </h2>

            {queries.length === 0 ? (
              <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-12 text-center">
                <Users className="w-16 h-16 text-purple-400 mx-auto mb-4" />
                <p className="text-purple-200 text-lg">No queries yet</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {queries.map((query) => (
                  <Card
                    key={query.id}
                    className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-6"
                    data-testid={`query-card-${query.id}`}
                  >
                    <div className="grid md:grid-cols-4 gap-6 items-start">
                      <div>
                        <p className="text-purple-400 text-sm mb-1">Name</p>
                        <p className="text-white font-semibold">{query.name}</p>
                      </div>
                      <div>
                        <p className="text-purple-400 text-sm mb-1">Contact</p>
                        <p className="text-white font-mono text-sm">{query.phone}</p>
                        {query.email && (
                          <p className="text-purple-300 text-xs mt-1">{query.email}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-purple-400 text-sm mb-1">Submitted</p>
                        <p className="text-white text-sm">
                          {query.submitted_at ? format(new Date(query.submitted_at), 'MMM dd, yyyy hh:mm a') : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-purple-400 text-sm mb-1">Status</p>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs border ${
                          query.status === 'responded' 
                            ? 'bg-green-500/20 text-green-300 border-green-500/30'
                            : 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                        }`}>
                          {query.status?.toUpperCase() || 'PENDING'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-purple-500/30">
                      <p className="text-purple-400 text-sm mb-2">Query / Question:</p>
                      <div className="bg-purple-950/50 border border-purple-500/30 rounded-lg p-4">
                        <p className="text-white leading-relaxed">{query.query}</p>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-purple-500/30 flex justify-end">
                      <a
                        href={`https://wa.me/${query.phone}?text=Hi%20${query.name},%20thank%20you%20for%20your%20query%20to%20Divine%20Quanta.`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg text-sm transition-colors"
                      >
                        Respond on WhatsApp
                      </a>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
